// src/layouts/AdminLayout.jsx
import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Divider,
  Chip,
  Menu,
  MenuItem,
  Tooltip,
  Collapse,
  CircularProgress
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Event as EventIcon,
  Poll as PollIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  BarChart as BarChartIcon,
  SportsEsports as SportsEsportsIcon,
  LogoutRounded as LogoutIcon,
  ExpandLess,
  ExpandMore,
  CalendarMonth as CalendarMonthIcon,
  AccountCircle as AccountCircleIcon,
  Campaign as CampaignIcon,
  BusinessCenter as BusinessCenterIcon,
  Policy as PolicyIcon,
  FolderShared as FolderSharedIcon,
  Inventory2 as Inventory2Icon,
  AdminPanelSettings as AdminPanelSettingsIcon
} from "@mui/icons-material";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import useAuthRole from "../hooks/useAuthRole";
import logo from "../assets/thinkers_logo_new.png";

const DRAWER_W = 240;
const MINI_W = 76;

// Small nav item component (keeps your styles)
const NavItem = ({ to, icon, label, collapsed, nested = false }) => (
  <ListItemButton
    component={NavLink}
    to={to}
    sx={{
      mx: 1,
      borderRadius: nested ? 1.25 : 1.5,
      px: 1.5,
      pl: !collapsed && nested ? 4 : 1.5,
      "&.active": {
        bgcolor: "action.selected",
        "& .MuiListItemIcon-root": { color: "primary.main" },
        "& .MuiListItemText-primary": { fontWeight: 700 }
      }
    }}
  >
    <ListItemIcon
      sx={{
        minWidth: nested ? 32 : 40,
        ml: nested && !collapsed ? 0.5 : 0
      }}
    >
      {icon}
    </ListItemIcon>
    {!collapsed && (
      <ListItemText
        primary={label}
        primaryTypographyProps={
          nested
            ? { fontSize: 13, color: "text.secondary" }
            : { fontSize: 14 }
        }
      />
    )}
  </ListItemButton>
);

const ALLOWED_ROLES = ["MAIN_ADMIN", "ADMIN", "HR"];

export default function AdminLayout() {
  const nav = useNavigate();
  const { user, role, loading } = useAuthRole();

  const [open, setOpen] = useState(true);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [hrOpen, setHrOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const collapsed = !open;
  const profileMenuOpen = Boolean(anchorEl);
  const roleLabels = {
  HR: "HR Manager",
  MAIN_ADMIN: "Developer",
  ADMIN: "ADMIN",
  };

  // Redirect logic: while loading show spinner; if not authenticated or role not allowed -> redirect to login
  useEffect(() => {
    if (loading) return;
    // not logged in
    if (!user) {
      nav("/login", { replace: true });
      return;
    }
    // logged in but role not allowed
    const roleNorm = String(role || "").toUpperCase();
    if (!ALLOWED_ROLES.includes(roleNorm)) {
      // optionally sign out to clear session: comment if you don't want that
      // signOut(auth).catch(() => {});
      nav("/login", { replace: true, state: { error: "Access denied" } });
    }
  }, [user, role, loading, nav]);

  // Show loader while resolving auth/role
  if (loading) {
    return (
      <Box sx={{ width: "100%", height: "60vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  // fallback: if somehow user is gone, redirect (defensive)
  if (!user) return null;

  const roleNorm = String(role || "").toUpperCase();
  const isHR = roleNorm === "HR";
  const canSeeRolesPage = ["MAIN_ADMIN", "ADMIN"].includes(roleNorm);

  const doLogout = async () => {
    await signOut(auth);
    nav("/login", { replace: true });
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      {/* ---------- TOP BAR ---------- */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          bgcolor: "background.paper",
          color: "text.primary",
          borderBottom: "1px solid",
          borderColor: "divider",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
        }}
      >
        <Toolbar sx={{ minHeight: 72, px: { xs: 2, md: 3 }, gap: 1.5 }}>
          {/* Menu toggle */}
          <IconButton onClick={() => setOpen(!open)} edge="start">
            <MenuIcon />
          </IconButton>

          {/* Logo + title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box
              component="img"
              src={logo}
              alt="TDO"
              sx={{ width: 40, height: 40, borderRadius: 1 }}
            />
            <Typography variant="h6" sx={{ fontWeight: 900, letterSpacing: "-0.01em" }}>
              ThinkersClub Admin
            </Typography>
            <Chip size="small" label="DEV" sx={{ ml: 0.5 }} />
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* ---------- Profile Section ---------- */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Tooltip title="View profile">
              <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: "primary.light",
                    color: "primary.main",
                    fontWeight: 700,
                    textTransform: "uppercase"
                  }}
                >
                  {user?.displayName?.[0] || (user?.email?.[0] ? user.email[0].toUpperCase() : "A")}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>

          {/* Profile dropdown */}
          <Menu
            anchorEl={anchorEl}
            open={profileMenuOpen}
            onClose={() => setAnchorEl(null)}
            PaperProps={{
              elevation: 4,
              sx: {
                mt: 1,
                minWidth: 200,
                borderRadius: 2,
                overflow: "hidden"
              }
            }}
          >
            <Box sx={{ p: 2, pb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {user?.displayName || user?.email || "Admin User"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {roleLabels[roleNorm] || roleNorm || "Admin"}
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                nav("/profile");
              }}
            >
              <ListItemIcon>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>View Profile</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                doLogout();
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText sx={{ color: "error.main" }}>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* ---------- DRAWER ---------- */}
      <Drawer
        variant="permanent"
        open={open}
        sx={{
          width: open ? DRAWER_W : MINI_W,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: open ? DRAWER_W : MINI_W,
            boxSizing: "border-box",
            borderRight: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            pt: 0.5,
            transition: "width 0.3s ease"
          }
        }}
      >
        <Toolbar />
        <Box sx={{ p: 1 }}>
          <List sx={{ display: "grid", gap: 0.25 }}>
            <NavItem
              to="/dashboard"
              icon={<DashboardIcon />}
              label="Dashboard"
              collapsed={collapsed}
            />
            <NavItem
              to="/employees"
              icon={<PeopleIcon />}
              label="Employees"
              collapsed={collapsed}
            />

            {/* Roles page: visible only to MAIN_ADMIN and ADMIN */}
            {canSeeRolesPage && (
              <NavItem
                to="/roles"
                icon={<AdminPanelSettingsIcon />}
                label="Roles"
                collapsed={collapsed}
              />
            )}

            {/* EVENTS COLLAPSIBLE */}
            <ListItemButton
              onClick={() => setEventsOpen(!eventsOpen)}
              sx={{ mx: 1, borderRadius: 1.5 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <EventIcon />
              </ListItemIcon>
              {!collapsed && <ListItemText primary="Events" />}
              {!collapsed && (eventsOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>

            <Collapse in={eventsOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <NavItem
                  to="/events/questionnaires"
                  icon={<PollIcon />}
                  label="Questionnaires"
                  collapsed={collapsed}
                  nested
                />
                <NavItem
                  to="/events/games"
                  icon={<SportsEsportsIcon />}
                  label="Games"
                  collapsed={collapsed}
                  nested
                />
              </List>
            </Collapse>

            <Divider sx={{ my: 1 }} />

            <NavItem
              to="/attendance"
              icon={<CalendarMonthIcon />}
              label="Attendance & Leaves"
              collapsed={collapsed}
            />

            {/* HR COLLAPSIBLE */}
            <ListItemButton
              onClick={() => setHrOpen(!hrOpen)}
              sx={{ mx: 1, borderRadius: 1.5 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <BusinessCenterIcon />
              </ListItemIcon>
              {!collapsed && <ListItemText primary="HR" />}
              {!collapsed && (hrOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>

            <Collapse in={hrOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <NavItem
                  to="/hr/policies"
                  icon={<PolicyIcon />}
                  label="HR Policies"
                  collapsed={collapsed}
                  nested
                />
                <NavItem
                  to="/hr/documents"
                  icon={<FolderSharedIcon />}
                  label="Employee Documents"
                  collapsed={collapsed}
                  nested
                />
                <NavItem
                  to="/hr/assets"
                  icon={<Inventory2Icon />}
                  label="Assets Register"
                  collapsed={collapsed}
                  nested
                />
              </List>
            </Collapse>

            <NavItem
              to="/announcements"
              icon={<CampaignIcon />}
              label="Announcements"
              collapsed={collapsed}
            />

            <NavItem
              to="/analytics"
              icon={<BarChartIcon />}
              label="Analytics & Export"
              collapsed={collapsed}
            />
            <NavItem
              to="/settings"
              icon={<SettingsIcon />}
              label="Settings"
              collapsed={collapsed}
            />
          </List>
        </Box>
      </Drawer>

      {/* ---------- MAIN CONTENT ---------- */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          minWidth: 0,
          bgcolor: "background.default"
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
}
